import { FormattedMessage } from 'react-intl';
import { MouseEvent, useEffect, useState } from 'react';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { SupplierMapping } from '@synergy/models';
import { defaultPageSize } from '@synergy/config';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import * as AttributesActions from './store/supplier-attribute-mapping-actions';
import { Attribute } from '../../../../../common/models/attribute';
import { MappedValues } from '../../../../../common/components/mapped-values';
import { MappingEditModal } from '../../../../../common/components/mapping-edit-modal/mapping-edit-modal';
import { bestLocaleSelector } from '../../../../../common/selectors/locale-selectors';
import { supplierAttributeMappingSelector } from './store/supplier-attribute-mapping-selectors';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

export const SupplierAttributeMapping = () => {
    const locale = useAppSelector(bestLocaleSelector);
    const { id: supplierIdParam } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();

    const attributes = useAppSelector(supplierAttributeMappingSelector);
    const dispatch = useThunkDispatch();

    const [selectedMapping, setSelectedMapping] = useState<SupplierMapping<Attribute>>();

    useEffect(() => {
        if (supplierIdParam && +supplierIdParam) {
            dispatch(AttributesActions.fetchSupplierAttributesMapping(+supplierIdParam, searchParams));
        }

        return () => {
            dispatch(AttributesActions.clearSupplierAttributeMappings);
        };
    }, [dispatch, supplierIdParam, searchParams]);

    const handleMappingEdit = (mapping: string[]): void => {
        if (supplierIdParam && +supplierIdParam && selectedMapping) {
            const supplierMapping: SupplierMapping<Attribute> = {
                ...selectedMapping,
                supplierMapping: mapping,
            };
            dispatch(AttributesActions.updateSupplierAttributeMapping(+supplierIdParam, supplierMapping));
        }
        setSelectedMapping(undefined);
    };

    const handleChangePage = (_event: MouseEvent<HTMLButtonElement> | null, page: number) => {
        if (supplierIdParam && +supplierIdParam) {
            searchParams.set('page', page.toString());
            dispatch(AttributesActions.fetchSupplierAttributesMapping(+supplierIdParam, searchParams));
        }
    };

    const handleChangeLimit = (limit: number) => {
        if (supplierIdParam && +supplierIdParam) {
            searchParams.set('limit', (limit || defaultPageSize).toString());
            searchParams.set('page', '1');
            dispatch(AttributesActions.fetchSupplierAttributesMapping(+supplierIdParam, searchParams));
        }
    };

    const sortOrder: 'asc' | 'desc' = 'desc' === searchParams.get('order') ? 'desc' : 'asc';

    const handleSort = (order: 'asc' | 'desc', sort: string) => {
        if (supplierIdParam && +supplierIdParam) {
            searchParams.set('sort', sort);
            searchParams.set('order', order);
            searchParams.set('locale', locale);
            dispatch(AttributesActions.fetchSupplierAttributesMapping(+supplierIdParam, searchParams));
        }
    };

    const columns: PaloColumn<SupplierMapping<Attribute>>[] = [
        {
            key: 'name',
            type: 'name',
            sortKey: 'name',
            head: <FormattedMessage id="app.table.column.name" />,
            cell: attribute => attribute.name,
        },
        {
            key: 'locale',
            type: 'classification',
            sortKey: 'locale',
            head: <FormattedMessage id="app.table.column.locale" />,
            cell: attribute => attribute.locale.toUpperCase(),
        },
        {
            key: 'defaultMapping',
            type: 'name',
            wrap: true,
            head: <FormattedMessage id="app.table.column.mapping.default" />,
            cell: attribute => <MappedValues values={attribute.defaultMapping} />,
        },
        {
            key: 'supplierMapping',
            type: 'name',
            wrap: true,
            head: <FormattedMessage id="app.table.column.mapping.own" />,
            cell: attribute => <MappedValues values={attribute.supplierMapping} />,
        },
    ];

    return (
        <>
            <PaloListView<SupplierMapping<Attribute>, number>
                embedded={true}
                columns={columns}
                items={attributes.items}
                getRowId={attribute => attribute.id}
                emptyLabel={<FormattedMessage id="app.table.column.mapping.own" />}
                sort={{ key: searchParams.get('sort') ?? 'name', order: sortOrder }}
                onSortChange={(key, order) => handleSort(order, key)}
                rowActions={attribute => [
                    { label: <FormattedMessage id="app.common.edit" />, onClick: () => setSelectedMapping(attribute) },
                ]}
                pagination={{
                    count: attributes.total,
                    limit: attributes.limit,
                    page: attributes.page,
                    onPageChange: page => handleChangePage(null, page),
                    onLimitChange: handleChangeLimit,
                }}
            />
            {selectedMapping && (
                <MappingEditModal
                    visible={!!selectedMapping}
                    mapping={selectedMapping.supplierMapping}
                    onClose={() => setSelectedMapping(undefined)}
                    onSubmit={handleMappingEdit}
                />
            )}
        </>
    );
};
